import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { invoiceApi } from "../services/invoiceApi";
import { settingsApi } from "../services/settingsApi";

const money = (value) => Number(Number(value || 0).toFixed(2));

export default function InvoicePrint() {
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [invoiceRes, settingsRes] = await Promise.all([
          invoiceApi.getById(id),
          settingsApi.getMy(),
        ]);

        setInvoice(invoiceRes.data?.invoice || null);
        setSettings(settingsRes.data?.settings || null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  const currency = invoice?.currency || settings?.invoiceCurrency || "USD";

  const totals = useMemo(() => {
    const subtotal = money(invoice?.subtotal || 0);
    const taxAmount = money(invoice?.taxAmount || 0);
    const discountAmount = money(invoice?.discountAmount || 0);
    const totalAmount = money(invoice?.totalAmount || 0);
    const paidAmount = money(invoice?.paidAmount || 0);
    const balanceAmount = money(invoice?.balanceAmount || totalAmount - paidAmount);

    return { subtotal, taxAmount, discountAmount, totalAmount, paidAmount, balanceAmount };
  }, [invoice]);

  const handlePrint = () => window.print();

  const handleSend = () => {
    const email = invoice?.vendor?.email || invoice?.vendor?.companyEmail;
    if (!email) {
      alert("Vendor email not found.");
      return;
    }

    const subject = encodeURIComponent(`Invoice ${invoice?.invoiceNumber || ""}`);
    const body = encodeURIComponent(
      `Dear ${invoice?.supplierName || "Customer"},\n\nPlease find your invoice ${invoice?.invoiceNumber || ""} attached.\n\nAmount Due: ${currency} ${totals.totalAmount.toFixed(2)}`
    );

    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <div className="p-6 text-slate-600 dark:text-slate-400">
        Loading invoice...
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="p-6 text-slate-600 dark:text-slate-400">
        Invoice not found.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6 text-slate-900 transition-colors duration-300 dark:bg-slate-950 dark:text-slate-100">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .print-wrap { box-shadow: none !important; border: none !important; }
        }
      `}</style>

      <div className="no-print mb-4 flex flex-wrap gap-3">
        <button
          onClick={handleSend}
          className="rounded-xl bg-green-600 px-5 py-3 text-sm font-medium text-white transition hover:bg-green-700"
        >
          Send invoice
        </button>
        <button
          onClick={handlePrint}
          className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          Download PDF
        </button>
      </div>

      <div className="print-wrap mx-auto max-w-5xl rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition-colors duration-300 dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-start justify-between gap-6">
          <div>
            {settings?.invoiceShowTitle !== false ? (
              <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
                {settings?.invoiceTitle || "INVOICE"}
              </h1>
            ) : null}

            <div className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Invoice #: {invoice.invoiceNumber}
            </div>
          </div>

          {settings?.invoiceShowLogo && settings?.logoDataUrl ? (
            <img
              src={settings.logoDataUrl}
              alt="Logo"
              className="h-20 w-20 rounded-2xl object-cover"
            />
          ) : null}
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <div>
            <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
              From
            </p>
            <p className="mt-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
              {settings?.companyName || invoice?.vendor?.name || "Your company"}
            </p>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {settings?.companyAddress || invoice?.vendor?.address || ""}
            </p>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {settings?.companyEmail || invoice?.vendor?.email || ""}
            </p>
          </div>

          <div>
            <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
              To
            </p>
            <p className="mt-2 text-lg font-semibold text-slate-900 dark:text-slate-100">
              {invoice.supplierName || "Client"}
            </p>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {invoice?.vendor?.address || ""}
            </p>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
              {invoice?.vendor?.email || ""}
            </p>
          </div>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-4">
          <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950">
            <div className="text-xs uppercase text-slate-500 dark:text-slate-400">
              Invoice Date
            </div>
            <div className="mt-2 font-semibold text-slate-900 dark:text-slate-100">
              {invoice.invoiceDate}
            </div>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950">
            <div className="text-xs uppercase text-slate-500 dark:text-slate-400">
              Due Date
            </div>
            <div className="mt-2 font-semibold text-slate-900 dark:text-slate-100">
              {invoice.dueDate || "-"}
            </div>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950">
            <div className="text-xs uppercase text-slate-500 dark:text-slate-400">
              Status
            </div>
            <div className="mt-2 font-semibold text-slate-900 dark:text-slate-100">
              {invoice.status}
            </div>
          </div>
          <div className="rounded-2xl bg-slate-50 p-4 dark:bg-slate-950">
            <div className="text-xs uppercase text-slate-500 dark:text-slate-400">
              Currency
            </div>
            <div className="mt-2 font-semibold text-slate-900 dark:text-slate-100">
              {currency}
            </div>
          </div>
        </div>

        <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800">
          <table className="min-w-full">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-800 dark:text-slate-300">
              <tr>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Qty</th>
                <th className="px-4 py-3">Unit Price</th>
                <th className="px-4 py-3">Tax %</th>
                <th className="px-4 py-3">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {(invoice.items || []).map((item) => {
                const qty = Number(item.quantity || 0);
                const unitPrice = Number(item.unitPrice || 0);
                const base = qty * unitPrice;
                const rate = Number(item.taxRate || 0);
                const lineTax = base * (rate / 100);
                const lineTotal = base + lineTax;

                return (
                  <tr
                    key={item.id}
                    className="bg-white text-slate-700 dark:bg-slate-900 dark:text-slate-300"
                  >
                    <td className="px-4 py-3">{item.description}</td>
                    <td className="px-4 py-3">{qty}</td>
                    <td className="px-4 py-3">
                      {currency} {unitPrice.toFixed(2)}
                    </td>
                    <td className="px-4 py-3">{rate}%</td>
                    <td className="px-4 py-3">
                      {currency} {lineTotal.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-8 flex justify-end">
          <div className="w-full max-w-md space-y-2 rounded-2xl bg-slate-50 p-5 dark:bg-slate-950">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600 dark:text-slate-400">Subtotal</span>
              <span className="font-semibold text-slate-900 dark:text-slate-100">
                {currency} {totals.subtotal.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-600 dark:text-slate-400">Tax</span>
              <span className="font-semibold text-slate-900 dark:text-slate-100">
                {currency} {totals.taxAmount.toFixed(2)}
              </span>
            </div>
            {totals.discountAmount > 0 ? (
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  Discount
                </span>
                <span className="font-semibold text-slate-900 dark:text-slate-100">
                  {currency} {totals.discountAmount.toFixed(2)}
                </span>
              </div>
            ) : null}
            <div className="flex items-center justify-between border-t border-slate-200 pt-2 dark:border-slate-800">
              <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                Amount Due
              </span>
              <span className="text-lg font-bold text-slate-900 dark:text-slate-100">
                {currency} {totals.totalAmount.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {invoice.notes ? (
          <div className="mt-8 rounded-2xl border border-slate-200 p-5 dark:border-slate-800">
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              Additional notes
            </p>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              {invoice.notes}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}